import { useCallback, useEffect, useState } from 'react';
import {
  setToken,
  clearToken,
  hasToken,
  getMe,
  getDirectoryShops,
  webLogin,
  webRegister,
  requestPasswordReset,
  confirmPasswordReset,
  resendConfirmation,
} from '../api';
import WebShop from './WebShop';
import TelegramLinkCard from '../components/TelegramLinkCard';
import type { DirectoryShop, Me, RepresentativeType } from '../types';

// ============================================================================
// Этап W1 (вариант B): веб-вход и веб-регистрация для обычного браузера (вне
// Telegram). Резервный канал на случай блокировки Telegram. Маршруты — по
// window.location.pathname: /login, /register, /reset-password (+ ?token=...).
// Подтверждение e-mail — по ссылке из письма (страницу рисует бэкенд).
// Полноценный веб-каталог/оформление заказа — отдельный будущий этап; здесь
// после входа показываем кабинет-заглушку со статусом и подсказкой.
// ============================================================================

const wrap: React.CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '32px 16px 64px',
};

const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  background: 'var(--tg-bg)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 16,
  padding: 28,
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const input: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 13px',
  borderRadius: 10,
  border: '1.5px solid var(--tg-hint)',
  background: 'var(--tg-secondary-bg)',
  color: 'var(--tg-text)',
  fontSize: 15,
  fontFamily: 'inherit',
  outline: 'none',
};

const label: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--tg-hint)',
  marginBottom: 4,
  display: 'block',
};

const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 };

const primaryBtn = (enabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '13px 0',
  border: 'none',
  borderRadius: 12,
  background: enabled ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
  color: enabled ? 'var(--tg-button-text)' : 'var(--tg-hint)',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: enabled ? 'pointer' : 'not-allowed',
  transition: 'background 0.15s',
});

const linkBtn: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--tg-link)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: 0,
};

const errorBox: React.CSSProperties = {
  background: '#ff3b3015',
  border: '1px solid #ff3b30',
  borderRadius: 10,
  padding: '10px 14px',
  marginBottom: 16,
  fontSize: 14,
  color: '#ff3b30',
};

const okBox: React.CSSProperties = {
  background: '#2e7d3215',
  border: '1px solid #2e7d32',
  borderRadius: 10,
  padding: '10px 14px',
  marginBottom: 16,
  fontSize: 14,
  color: '#2e7d32',
};

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Извлекаем человекочитаемый текст ошибки из «{status}: {json|text}».
function parseError(msg: string): string {
  const m = msg.match(/^\d+:\s*(.*)$/s);
  const body = m ? m[1] : msg;
  try {
    const j = JSON.parse(body);
    if (typeof j.detail === 'string') return j.detail;
    if (Array.isArray(j.detail) && j.detail[0]?.msg) return j.detail[0].msg;
  } catch { /* не JSON — отдаём как есть */ }
  return body || 'Что-то пошло не так';
}

type View = 'login' | 'register' | 'reset-request' | 'reset-confirm' | 'verify-notice' | 'cabinet';

// Роли, которым доступна панель управления /admin/ (зеркалит _ADMIN_LOGIN_ROLES
// на бэкенде). Клиент (client) сюда НЕ входит — у него нет доступа в админку, и
// именно ему (Этап W2) показываем веб-магазин вместо кабинета-заглушки.
const ADMIN_PANEL_ROLES = ['admin', 'manager', 'accountant', 'boss', 'network_manager'];

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--tg-button)' }}>GreenHouse</div>
      <div className="hint" style={{ fontSize: 13, marginTop: 2 }}>Доставка для кофеен</div>
    </div>
  );
}

export default function WebAuth() {
  // Маршрутизация без react-router: смотрим на pathname и слушаем popstate.
  const [path, setPath] = useState(window.location.pathname);
  const navigate = useCallback((p: string) => {
    window.history.pushState({}, '', p);
    setPath(p);
  }, []);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Состояние авторизованного пользователя (после успешного входа/регистрации).
  const [me, setMe] = useState<Me | null>(null);

  // Этап W1 (вариант B): восстановление сессии при загрузке страницы. Токен
  // лежит в localStorage, но `me` — это состояние в памяти, которое сбрасывается
  // при каждом обновлении/возврате на сайт. Раньше из-за этого пользователь с
  // действующим токеном попадал на форму /login вместо кабинета. Теперь, если
  // токен есть, подтягиваем /me и сразу показываем кабинет (или «турникет» почты).
  const [restoring, setRestoring] = useState<boolean>(() => hasToken());

  useEffect(() => {
    if (!hasToken()) return;
    let alive = true;
    getMe()
      .then(({ data }) => { if (alive) setMe(data); })
      .catch(() => { clearToken(); /* токен протух/невалиден — остаёмся на форме входа */ })
      .finally(() => { if (alive) setRestoring(false); });
    return () => { alive = false; };
  }, []);

  // Выход: чистим токен и возвращаемся на форму входа.
  const logout = useCallback(() => {
    clearToken();
    setMe(null);
    navigate('/login');
  }, [navigate]);

  const resetToken = new URLSearchParams(window.location.search).get('token');

  // Определяем текущий экран. Если пользователь вошёл — кабинет/подтверждение почты.
  let view: View;
  if (me) {
    view = me.email_verified ? 'cabinet' : 'verify-notice';
  } else if (path.startsWith('/register')) {
    view = 'register';
  } else if (path.startsWith('/reset-password')) {
    view = resetToken ? 'reset-confirm' : 'reset-request';
  } else {
    view = 'login';
  }

  // После успешной авторизации подтягиваем /me и решаем, куда вести.
  const afterAuth = useCallback(async (token: string) => {
    setToken(token);
    try {
      const { data } = await getMe();
      setMe(data);
    } catch {
      // /me не критичен — покажем кабинет-заглушку без деталей.
      setMe({
        id: '', telegram_id: null, telegram_username: null, full_name: null,
        phone: null, email: null, status: 'pending', email_verified: false,
        roles: [], client_status: null, registration: null,
        privacy: { current_version: '', accepted_version: null, needs_acceptance: false },
      });
    }
  }, []);

  // Этап W2 (веб-заказы): подтверждённый обычный клиент (роль client, без доступа
  // в админку) получает полноценный веб-магазин вместо кабинета-заглушки. Сотрудники
  // (staff/франшиза) и неавторизованные сюда не попадают — у них кабинет/формы.
  if (!restoring && me && view === 'cabinet') {
    const canAdmin = (me.roles ?? []).some(r => ADMIN_PANEL_ROLES.includes(r));
    if (!canAdmin && me.client_status) {
      return <WebShop onLogout={logout} />;
    }
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <Logo />
        {restoring && !me ? (
          <p className="hint" style={{ textAlign: 'center', padding: '24px 0' }}>Загрузка…</p>
        ) : (
          <>
            {view === 'login' && <LoginView navigate={navigate} onAuth={afterAuth} />}
            {view === 'register' && <RegisterView navigate={navigate} onAuth={afterAuth} />}
            {view === 'reset-request' && <ResetRequestView navigate={navigate} />}
            {view === 'reset-confirm' && <ResetConfirmView navigate={navigate} token={resetToken!} />}
            {view === 'verify-notice' && <VerifyNotice email={me?.email ?? null} onLogout={logout} onRefresh={async () => {
              try { const { data } = await getMe(); setMe(data); } catch { /* игнор */ }
            }} />}
            {view === 'cabinet' && <Cabinet me={me!} onLogout={logout} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── Вход ─────────────────────────────────────────────────────────────────────
function LoginView({ navigate, onAuth }: { navigate: (p: string) => void; onAuth: (t: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = emailRe.test(email.trim()) && password.length >= 1 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await webLogin(email.trim(), password);
      onAuth(data.access_token);
    } catch (e) {
      setErr(parseError((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Вход на сайт</h2>
      {err && <div style={errorBox}>{err}</div>}
      <div style={field}>
        <label style={label}>E-mail</label>
        <input style={input} type="email" inputMode="email" autoCapitalize="none"
          value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} placeholder="you@example.com" />
      </div>
      <div style={field}>
        <label style={label}>Пароль</label>
        <input style={input} type="password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Ваш пароль" />
      </div>
      <button style={primaryBtn(canSubmit)} disabled={!canSubmit} onClick={submit}>
        {busy ? 'Вход…' : 'Войти'}
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <button style={linkBtn} onClick={() => navigate('/register')}>Регистрация</button>
        <button style={linkBtn} onClick={() => navigate('/reset-password')}>Забыли пароль?</button>
      </div>
    </>
  );
}

// ── Регистрация ────────────────────────────────────────────────────────────
function RegisterView({ navigate, onAuth }: { navigate: (p: string) => void; onAuth: (t: string) => void }) {
  const [repType, setRepType] = useState<RepresentativeType>('greenhouse');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [privacy, setPrivacy] = useState(false);

  const [shops, setShops] = useState<DirectoryShop[]>([]);
  const [search, setSearch] = useState('');
  const [shopId, setShopId] = useState<string | null>(null);
  const [wishText, setWishText] = useState('');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getDirectoryShops().then(({ data }) => setShops(data)).catch(() => { /* список необязателен */ });
  }, []);

  const emailValid = emailRe.test(email.trim());
  const phoneValid = phone.trim().length >= 5;
  const pwdValid = password.length >= 8;
  const pwdMatch = password === password2;
  const canSubmit = !!lastName.trim() && !!firstName.trim() && emailValid && phoneValid
    && pwdValid && pwdMatch && privacy && !busy;

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    const base = q ? shops.filter(s => s.name.toLowerCase().includes(q) || s.address.toLowerCase().includes(q)) : shops;
    return base.slice(0, 20);
  })();
  const selected = shops.find(s => s.id === shopId) ?? null;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await webRegister({
        representative_type: repType,
        last_name: lastName.trim(),
        first_name: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        privacy_accepted: privacy,
        ...(repType === 'greenhouse'
          ? (shopId ? { wished_shop_id: shopId } : wishText.trim() ? { wished_shop_text: wishText.trim() } : {})
          : {}),
      });
      onAuth(data.access_token);
    } catch (e) {
      setErr(parseError((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Регистрация</h2>
      {err && <div style={errorBox}>{err}</div>}

      <div style={field}>
        <label style={label}>Кто вы?</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['greenhouse', 'supplier'] as RepresentativeType[]).map(t => (
            <button key={t} onClick={() => setRepType(t)}
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${repType === t ? 'var(--tg-button)' : 'var(--tg-hint)'}`,
                background: repType === t ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                color: repType === t ? 'var(--tg-button-text)' : 'var(--tg-text)',
              }}>
              {t === 'greenhouse' ? 'Представитель GreenHouse' : 'Представитель поставщика'}
            </button>
          ))}
        </div>
      </div>

      <div style={field}>
        <label style={label}>Фамилия</label>
        <input style={input} value={lastName} onChange={e => setLastName(e.target.value)} maxLength={80} placeholder="Иванов" />
      </div>
      <div style={field}>
        <label style={label}>Имя</label>
        <input style={input} value={firstName} onChange={e => setFirstName(e.target.value)} maxLength={80} placeholder="Иван" />
      </div>
      <div style={field}>
        <label style={label}>E-mail</label>
        <input style={{ ...input, borderColor: email && !emailValid ? '#ff3b30' : 'var(--tg-hint)' }}
          type="email" inputMode="email" autoCapitalize="none"
          value={email} onChange={e => setEmail(e.target.value)} maxLength={255} placeholder="you@example.com" />
      </div>
      <div style={field}>
        <label style={label}>Мобильный телефон</label>
        <input style={{ ...input, borderColor: phone && !phoneValid ? '#ff3b30' : 'var(--tg-hint)' }}
          inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} maxLength={32} placeholder="+7 999 000-00-00" />
      </div>
      <div style={field}>
        <label style={label}>Пароль (мин. 8 символов)</label>
        <input style={{ ...input, borderColor: password && !pwdValid ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password" autoComplete="new-password" value={password}
          onChange={e => setPassword(e.target.value)} maxLength={128} />
      </div>
      <div style={field}>
        <label style={label}>Повторите пароль</label>
        <input style={{ ...input, borderColor: password2 && !pwdMatch ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password" autoComplete="new-password" value={password2}
          onChange={e => setPassword2(e.target.value)} maxLength={128} />
        {password2 && !pwdMatch && <span style={{ fontSize: 12, color: '#ff3b30' }}>Пароли не совпадают</span>}
      </div>

      {repType === 'greenhouse' && (
        <div style={field}>
          <label style={label}>Ваша точка (необязательно)</label>
          {selected ? (
            <div style={{ border: '1.5px solid var(--tg-button)', borderRadius: 10, padding: '10px 12px', background: 'var(--tg-secondary-bg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{selected.name}</span>
                <button style={linkBtn} onClick={() => { setShopId(null); setSearch(''); }}>Изменить</button>
              </div>
              <div className="hint" style={{ fontSize: 12, marginTop: 4 }}>{selected.address}</div>
            </div>
          ) : (
            <>
              <input style={input} placeholder="Поиск по названию или адресу"
                value={search} onChange={e => setSearch(e.target.value)} />
              {search.trim() && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                  {filtered.length === 0
                    ? <span className="hint" style={{ fontSize: 13 }}>Ничего не найдено — опишите точку ниже.</span>
                    : filtered.map(s => (
                      <button key={s.id} onClick={() => { setShopId(s.id); setWishText(''); }}
                        style={{ textAlign: 'left', border: '1px solid var(--tg-hint)', borderRadius: 8, padding: '8px 10px', background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                        <div className="hint" style={{ fontSize: 12 }}>{s.address}</div>
                      </button>
                    ))}
                </div>
              )}
              <input style={{ ...input, marginTop: 8 }} placeholder="Не нашли? Опишите словами"
                value={wishText} onChange={e => setWishText(e.target.value)} maxLength={500} />
            </>
          )}
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.45, marginBottom: 16 }}>
        <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}
          style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1, accentColor: 'var(--tg-button)' }} />
        <span>Я ознакомлен(а) с <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--tg-link)' }}>Политикой обработки персональных данных</a> и даю согласие на обработку.</span>
      </label>

      <button style={primaryBtn(canSubmit)} disabled={!canSubmit} onClick={submit}>
        {busy ? 'Отправка…' : 'Зарегистрироваться'}
      </button>
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button style={linkBtn} onClick={() => navigate('/login')}>Уже есть аккаунт? Войти</button>
      </div>
    </>
  );
}

// ── Запрос сброса пароля ──────────────────────────────────────────────────
function ResetRequestView({ navigate }: { navigate: (p: string) => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = emailRe.test(email.trim()) && !busy;
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } catch (e) {
      setErr(parseError((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Проверьте почту</h2>
        <div style={okBox}>Если такой e-mail зарегистрирован, мы отправили ссылку для сброса пароля.</div>
        <button style={linkBtn} onClick={() => navigate('/login')}>← Вернуться ко входу</button>
      </>
    );
  }

  return (
    <>
      <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Сброс пароля</h2>
      <p className="hint" style={{ margin: '0 0 16px', lineHeight: 1.4 }}>
        Укажите e-mail — пришлём ссылку для установки нового пароля.
      </p>
      {err && <div style={errorBox}>{err}</div>}
      <div style={field}>
        <label style={label}>E-mail</label>
        <input style={input} type="email" inputMode="email" autoCapitalize="none"
          value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} placeholder="you@example.com" />
      </div>
      <button style={primaryBtn(canSubmit)} disabled={!canSubmit} onClick={submit}>
        {busy ? 'Отправка…' : 'Прислать ссылку'}
      </button>
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <button style={linkBtn} onClick={() => navigate('/login')}>← Вернуться ко входу</button>
      </div>
    </>
  );
}

// ── Установка нового пароля по токену из письма ─────────────────────────────
function ResetConfirmView({ navigate, token }: { navigate: (p: string) => void; token: string }) {
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pwdValid = password.length >= 8;
  const pwdMatch = password === password2;
  const canSubmit = pwdValid && pwdMatch && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (e) {
      setErr(parseError((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Пароль обновлён</h2>
        <div style={okBox}>Теперь можно войти с новым паролем.</div>
        <button style={primaryBtn(true)} onClick={() => { window.history.replaceState({}, '', '/login'); navigate('/login'); }}>
          Перейти ко входу
        </button>
      </>
    );
  }

  return (
    <>
      <h2 style={{ margin: '0 0 16px', fontSize: 19 }}>Новый пароль</h2>
      {err && <div style={errorBox}>{err}</div>}
      <div style={field}>
        <label style={label}>Новый пароль (мин. 8 символов)</label>
        <input style={{ ...input, borderColor: password && !pwdValid ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password" autoComplete="new-password" value={password}
          onChange={e => setPassword(e.target.value)} maxLength={128} />
      </div>
      <div style={field}>
        <label style={label}>Повторите пароль</label>
        <input style={{ ...input, borderColor: password2 && !pwdMatch ? '#ff3b30' : 'var(--tg-hint)' }}
          type="password" autoComplete="new-password" value={password2}
          onChange={e => setPassword2(e.target.value)} maxLength={128} />
        {password2 && !pwdMatch && <span style={{ fontSize: 12, color: '#ff3b30' }}>Пароли не совпадают</span>}
      </div>
      <button style={primaryBtn(canSubmit)} disabled={!canSubmit} onClick={submit}>
        {busy ? 'Сохранение…' : 'Сохранить пароль'}
      </button>
    </>
  );
}

// ── Экран «Подтвердите e-mail» ──────────────────────────────────────────────
function VerifyNotice({ email, onRefresh, onLogout }: { email: string | null; onRefresh: () => void; onLogout: () => void }) {
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const resend = async () => {
    if (resending) return;
    setResending(true);
    setResendMsg(null);
    try {
      const { data } = await resendConfirmation();
      if (data.already_verified) {
        await onRefresh();
        return;
      }
      setResendMsg(data.message || 'Письмо отправлено повторно.');
    } catch {
      setResendMsg('Не удалось отправить письмо. Попробуйте позже.');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <h2 style={{ margin: '0 0 12px', fontSize: 19 }}>Подтвердите e-mail</h2>
      <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.5 }}>
        Мы отправили письмо{email ? <> на <b>{email}</b></> : ''}. Перейдите по ссылке из письма,
        чтобы завершить регистрацию. E-mail нужен для входа при проблемах с Telegram и для
        системных уведомлений.
      </p>
      <button style={primaryBtn(!busy)} disabled={busy} onClick={async () => { setBusy(true); await onRefresh(); setBusy(false); }}>
        {busy ? 'Проверяем…' : 'Я подтвердил — обновить'}
      </button>
      <button
        style={{
          width: '100%',
          marginTop: 10,
          padding: '12px 0',
          border: '1px solid var(--tg-button)',
          borderRadius: 12,
          background: 'transparent',
          color: 'var(--tg-button)',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: resending ? 'not-allowed' : 'pointer',
          opacity: resending ? 0.6 : 1,
        }}
        disabled={resending}
        onClick={resend}
      >
        {resending ? 'Отправляем…' : 'Отправить письмо ещё раз'}
      </button>
      {resendMsg && (
        <p style={{ marginTop: 12, fontSize: 13, lineHeight: 1.45 }}>{resendMsg}</p>
      )}
      <p className="hint" style={{ marginTop: 14, fontSize: 12, lineHeight: 1.4 }}>
        Письмо не пришло? Проверьте папку «Спам». Если письма нет — напишите менеджеру.
      </p>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <button style={linkBtn} onClick={onLogout}>Войти под другим аккаунтом</button>
      </div>
    </>
  );
}

// ── Кабинет-заглушка после входа ────────────────────────────────────────────
// Показывается сотрудникам/франшизе (staff). Обычный клиент сюда не попадает —
// для него рендерится веб-магазин (WebShop), см. ранний return в WebAuth.
function Cabinet({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const statusLabel: Record<string, string> = {
    pending: 'на рассмотрении', approved: 'одобрена', rejected: 'отклонена', blocked: 'заблокирована',
  };
  // Если у аккаунта есть staff-роль — показываем переход в панель управления,
  // чтобы не набирать адрес /admin/ вручную. Это полноценная навигация в другое
  // SPA (своя авторизация), поэтому используем обычную ссылку.
  const canOpenAdmin = (me.roles ?? []).some(r => ADMIN_PANEL_ROLES.includes(r));
  return (
    <>
      <h2 style={{ margin: '0 0 8px', fontSize: 19 }}>Вы вошли в GreenHouse</h2>
      <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.5 }}>
        {me.full_name ? <>Здравствуйте, <b>{me.full_name}</b>! </> : ''}
        {me.client_status
          ? <>Ваша заявка: <b>{statusLabel[me.client_status] ?? me.client_status}</b>.</>
          : 'Аккаунт активен.'}
      </p>
      <div style={okBox}>E-mail подтверждён — резервный вход на сайт работает.</div>
      {!me.telegram_id && <TelegramLinkCard />}

      {canOpenAdmin && (
        <a
          href="/admin/"
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            padding: '13px 0',
            borderRadius: 12,
            background: 'var(--tg-button)',
            color: 'var(--tg-button-text)',
            fontSize: 15,
            fontWeight: 600,
            textAlign: 'center',
            textDecoration: 'none',
            marginBottom: 14,
          }}
        >
          Перейти в панель управления →
        </a>
      )}

      <p className="hint" style={{ fontSize: 13, lineHeight: 1.45 }}>
        Оформление заказов сейчас доступно в Telegram Mini App. Полноценный заказ через сайт
        появится позже — пока сайт работает как резервный вход и регистрация.
      </p>
      <div style={{ textAlign: 'center', marginTop: 18 }}>
        <button style={linkBtn} onClick={onLogout}>Выйти</button>
      </div>
    </>
  );
}
